import { useContext, useEffect, useMemo, useRef } from 'react';

import {
  faArrowLeftToLine,
  faArrowRightToLine,
} from '@awesome.me/kit-f3dc60f0d0/icons/classic/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import classNames from 'classnames';
import { useAtomValue } from 'jotai';
import { useParams } from 'react-router-dom';

import { ExternalPatientContext } from '@/app/external/constants';
import { VideaDevTool } from '@/common-components/composed/activeApplicationPath/videaDevTool/VideaDevTool';
import {
  isActionableViewAtom,
  selectTeethAtom,
} from '@/modules/patientList/atoms';
import { PatientStripOdontogram } from '@/modules/patientList/patientStrip/patientStripOdontogram/patientStripOdontogram';
import { getPatientAge } from '@/modules/patientList/patientStrip/patientStripRedesigned/patientInfoSheet/PatientInfo';
import {
  actionableStatuses,
  allStatuses,
} from '@/modules/patientList/patientStrip/patientStripRedesigned/TreatmentCounts';
import { useIsVDAPracticeWithNonVDAAnalysis } from '@/utils/hooks/useIsVDAPracticeWithNonVDAAnalysis';
import { useWindowSize } from '@/utils/hooks/useWindowSize';
import { useUniversalToFDI } from '@/utils/internationalization/useUniversalToFDI';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';
import type { ValidUniversalToothId } from '@videa/utils';

import { useGetPatientImageStatuses } from '../../../react-query/useGetPatientImageStatuses';
import { usePreferences } from '../../../react-query/usePreferences';
import type { TVisitSummary } from '../../../react-query/useVisitSummaries';
import { AIHasReran, RerunAi } from '../findingSidebar/RerunAi';
import type { ImageObject } from '../types';

import { BoneLevel } from './BoneLevel';
import {
  getQuadrantByToothNumber,
  getVisitSidebarView,
  useFilteredVisitSummaryTeeth,
} from './helpers';
import { HistoricalAnalysisStatus } from './HistoricalAnalysisStatus';
import { ToothDamage } from './ToothDamage';
import { ViewerRedesignButton } from './ViewerRedesignButton';
import { VisitSidebarFooter } from './VisitSidebarFooter';
import { VisitSidebarRecommendation } from './VisitSidebarRecommendation';

const BREAKPOINT_HEIGHT_EXTRA_SMALL = 570;
const BREAKPOINT_WIDTH_EXTRA_SMALL = 310;
const BREAKPOINT_WIDTH_MEDIUM = 768;
const BREAKPOINT_WIDTH_LARGE = 1024;
const SMALL_SCREEN_SCALE_FACTOR = 0.8;
const LARGE_SCREEN_SCALE_FACTOR = 1;

type VisitSidebarProps = {
  activeImage?: ImageObject;
  isExpandedSidebar: boolean;
  dob?: string;
  isInsuranceClaimLoading?: boolean;
  isLoading: boolean;
  onOpenInsuranceClaimPdf?: () => void;
  patientName?: string;
  setIsExpandedSidebar?: (isExpandedSidebar: boolean) => void;
  sortedAndFilteredImages?: ImageObject[];
  visitSummary?: TVisitSummary;
};

export const VisitSidebar = ({
  activeImage,
  dob,
  isExpandedSidebar,
  isInsuranceClaimLoading,
  isLoading,
  onOpenInsuranceClaimPdf,
  patientName,
  setIsExpandedSidebar,
  sortedAndFilteredImages,
  visitSummary,
}: VisitSidebarProps) => {
  const selectedTeeth = useAtomValue(selectTeethAtom);
  const isActionableView = useAtomValue(isActionableViewAtom);

  const externalPatientContext = useContext(ExternalPatientContext);
  const isIframe = externalPatientContext.isIframe;
  const { id: selectedPatientId } = useParams<{ id: string }>();
  const { trackEvent } = useTrackEvent();
  const isVDAPracticeWithNonVDAAnalysis = useIsVDAPracticeWithNonVDAAnalysis(
    activeImage?.imageId ? [activeImage] : sortedAndFilteredImages,
  );
  const filteredVisitSummaryTeeth = useFilteredVisitSummaryTeeth(visitSummary);
  const { displayToothNumber, displayLocalizedQuadrantShortName } =
    useUniversalToFDI();
  const { windowWidth, windowHeight } = useWindowSize();

  // Get preferences
  const { data: prefs } = usePreferences();
  const allowRerunAnalysesFromBridgeLinkEvent =
    prefs?.allowRerunAnalysesFromBridgeLinkEvent;
  const enableBatchVisitSummariesProcessing =
    prefs?.enableBatchVisitSummariesProcessing;

  // Get patient ID for image status check
  const patientId = selectedPatientId ?? externalPatientContext.patientId;

  // Track if we've ever seen pending analyses
  const hasEverSeenPending = useRef(false);

  // Fetch image statuses only if preference is enabled
  const { data: imageStatusData } = useGetPatientImageStatuses(
    allowRerunAnalysesFromBridgeLinkEvent && patientId
      ? { videaPatientId: patientId }
      : undefined,
  );

  // Calculate analysis status
  const analysisStatus = useMemo(() => {
    if (!imageStatusData?.imageStatuses) {
      return { isAnalyzing: false, isComplete: false, shouldShow: false };
    }

    const totalPending = imageStatusData.imageStatuses.reduce(
      (sum, status) => sum + status.pending,
      0,
    );
    const totalCompleted = imageStatusData.imageStatuses.reduce(
      (sum, status) => sum + status.completed,
      0,
    );
    // Track if we've ever seen pending analyses
    if (totalPending > 0) {
      hasEverSeenPending.current = true;
    }

    // Only show if there are pending analyses or if we just completed some analyses
    // Don't show if everything was already complete from the start
    const shouldShow =
      totalPending > 0 ||
      (totalPending === 0 && totalCompleted > 0 && hasEverSeenPending.current);

    return {
      isAnalyzing: totalPending > 0,
      isComplete: totalPending === 0 && totalCompleted > 0,
      shouldShow,
    };
  }, [imageStatusData]);

  const selectedToothLabel = () => {
    if (!selectedTeeth.length) return null;

    const selectedToothQuadrant = getQuadrantByToothNumber(
      selectedTeeth[0] as ValidUniversalToothId,
    );

    return (
      <>
        {' - '}
        <Trans>Tooth </Trans>
        {displayToothNumber(selectedTeeth[0])}
        {selectedToothQuadrant &&
          `, ${displayLocalizedQuadrantShortName(selectedToothQuadrant)}`}
      </>
    );
  };

  const getScaleFactor = () => {
    if (externalPatientContext.isIframe) {
      if (
        windowWidth < BREAKPOINT_WIDTH_EXTRA_SMALL ||
        windowHeight < BREAKPOINT_HEIGHT_EXTRA_SMALL
      ) {
        return SMALL_SCREEN_SCALE_FACTOR;
      }
      return LARGE_SCREEN_SCALE_FACTOR;
    }

    return windowWidth >= BREAKPOINT_WIDTH_LARGE
      ? LARGE_SCREEN_SCALE_FACTOR
      : SMALL_SCREEN_SCALE_FACTOR;
  };

  const visitSummarySelectedTooth = visitSummary?.teeth.find(
    (tooth) => !!selectedTeeth[0] && tooth.toothId === selectedTeeth[0],
  );

  const showToothDetails = !!selectedTeeth.length && !activeImage;

  useEffect(() => {
    setIsExpandedSidebar?.(windowWidth >= BREAKPOINT_WIDTH_MEDIUM);
  }, [windowWidth, setIsExpandedSidebar]);

  // Hide visit sidebar and sidebar toggle for dental photos
  if (activeImage?.detectedImageType === 'COLOR_IMAGE') {
    return null;
  }

  return (
    <div
      className={classNames(
        'vai-relative vai-bg-sidebar-background vai-flex vai-flex-col vai-justify-between vai-z-[200]',
        {
          'vai-h-full vai-pt-1': !isIframe,
          'vai-w-[300px] lg:vai-w-[360px]': isExpandedSidebar && !isIframe,
          'vai-w-5': !isExpandedSidebar,
          'vai-w-full vai-mt-1 vai-flex-1 vai-overflow-y-auto': isIframe,
        },
      )}
    >
      {/* Using two nested divs because apparently you can't have overflow scroll 
      on one axis and overflow visible on the other. First div allows overflow for 
      sidebar toggle. Second div manages scrolling of sidebar content. */}
      <div className="vai-h-full vai-flex vai-flex-col vai-bg-sidebar-background">
        {/* Icon toggle to collapse/expand the sidebar */}
        {!isIframe && (
          <div className="vai-bg-background-dark vai-h-6 vai-w-6 vai-absolute vai-top-1 -vai-left-3 vai-rounded-full vai-inline-flex vai-items-center vai-justify-center vai-z-[200] vai-cursor-pointer">
            <FontAwesomeIcon
              color="white"
              icon={isExpandedSidebar ? faArrowRightToLine : faArrowLeftToLine}
              onClick={() => {
                trackEvent(mixpanelEvents.toggleSidebar, {
                  isSidebarExpanded: !isExpandedSidebar,
                  estimatedVisitId: visitSummary?.estimatedVisitId,
                  unifiedAppointmentId: visitSummary?.unifiedAppointmentId,
                  view: getVisitSidebarView(activeImage, selectedTeeth),
                });
                setIsExpandedSidebar?.(!isExpandedSidebar);
              }}
            />
          </div>
        )}
        {isExpandedSidebar && (
          <>
            <div className="vai-min-h-0 vai-flex-1 vai-overflow-y-auto vai-px-2">
              <ViewerRedesignButton />

              <VideaDevTool
                activeImage={activeImage}
                sortedAndFilteredImages={sortedAndFilteredImages || []}
              />

              <PatientStripOdontogram
                activeImageId={activeImage?.imageId}
                age={getPatientAge(dob)}
                module="image-viewer"
                selectedStatuses={
                  isActionableView ? actionableStatuses : allStatuses
                }
                visitSummary={visitSummary}
                scaleFactor={getScaleFactor()}
              />
              {analysisStatus.shouldShow &&
                enableBatchVisitSummariesProcessing && (
                  <HistoricalAnalysisStatus
                    isAnalyzing={analysisStatus.isAnalyzing}
                    isComplete={analysisStatus.isComplete}
                  />
                )}
              <div className="vai-mt-4 [@media(max-height:570px)]:vai-mt-2 vai-border vai-border-[#27272A] vai-rounded-[6px] vai-overflow-hidden">
                <div className="vai-p-3 [@media(max-height:570px)]:vai-p-2 vai-bg-[#0A0A0A]">
                  <p className="vai-typography-small vai-text-[#FAFAFA]">
                    <Trans>AI Summary</Trans>
                    {/* In the case when we have selected an image from tooth selection, we don't want to show the tooth label. */}
                    {showToothDetails && selectedToothLabel()}
                  </p>
                </div>
                <div className="vai-p-3 [@media(max-height:570px)]:vai-p-2 vai-flex vai-flex-col vai-gap-3 [@media(max-height:570px)]:vai-gap-2">
                  {activeImage && (
                    <p className="vai-font-inter vai-text-[12px] vai-text-zinc-300">
                      <Trans>
                        AI analysis results summarized across all images from
                        this visit:
                      </Trans>
                    </p>
                  )}
                  {showToothDetails && (
                    <VisitSidebarRecommendation
                      visitSummary={visitSummary}
                      visitSummaryTooth={visitSummarySelectedTooth}
                    />
                  )}
                  <ToothDamage
                    activeImage={activeImage}
                    estimatedVisitId={visitSummary?.estimatedVisitId}
                    isLoading={isLoading}
                    teeth={filteredVisitSummaryTeeth}
                    unifiedAppointmentId={visitSummary?.unifiedAppointmentId}
                  />
                  <BoneLevel
                    activeImage={activeImage}
                    estimatedVisitId={visitSummary?.estimatedVisitId}
                    isLoading={isLoading}
                    teeth={filteredVisitSummaryTeeth}
                    unifiedAppointmentId={visitSummary?.unifiedAppointmentId}
                    visitSummary={visitSummary}
                  />
                </div>
              </div>

              {isVDAPracticeWithNonVDAAnalysis ? (
                <RerunAi
                  estimatedVisitId={visitSummary?.estimatedVisitId}
                  images={sortedAndFilteredImages}
                  selectedPatientId={
                    selectedPatientId ?? externalPatientContext.patientId
                  }
                />
              ) : (
                <AIHasReran />
              )}
            </div>

            <div className="vai-flex-shrink-0 vai-mt-4 [@media(max-height:570px)]:vai-mt-2">
              <VisitSidebarFooter
                activeImageId={activeImage?.imageId}
                dob={dob}
                isInsuranceClaimLoading={!!isInsuranceClaimLoading}
                isLoading={isLoading}
                onOpenInsuranceClaimPdf={onOpenInsuranceClaimPdf}
                patientName={patientName}
                visitSummary={visitSummary}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};
